/*
 * rolly-email-signup.js — every email sign-up form in the storefront theme.
 * Loaded once, deferred, from layout/theme.liquid.
 *
 *  1. rolly_klaviyo.subscribe()  one Klaviyo Client API request (form 2)
 *  2. Klaviyo newsletter form    snippets/klaviyo-newsletter-form.liquid (footer
 *                                 setting, currently switched off)
 *  3. Notify form                snippets/pdp-notify.liquid (product page:
 *                                 sold out in the UK, or visitor outside the UK)
 *  4. "Join the list" form       sections/newsletter.liquid
 *
 * Forms 3 and 4 are Shopify customer forms: each sign-up becomes a Shopify
 * customer, and Klaviyo's "Sync data from Shopify" copies it into Klaviyo.
 * This file checks the email first (same wording on both) and sorts out which
 * form shows the result after Shopify's redirect.
 *
 * Not covered: sections/coming-soon.liquid keeps its own copy because the
 * password page is live and must not change.
 */
(function () {
  if (window.rolly_klaviyo) return;

  var REVISION = '2026-01-15';
  var EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  /* ---------- 1 · Shared subscribe ---------- */

  // Resolves on 202 (Klaviyo's only success status for this endpoint); rejects otherwise.
  function subscribe(opts) {
    var properties = Object.assign({
      signup_source: opts.source,
      signup_date: new Date().toISOString().split('T')[0],
      signup_url: window.location.href
    }, opts.properties || {});

    return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(opts.key), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: REVISION },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: opts.customSource || opts.source,
            profile: { data: { type: 'profile', attributes: { email: opts.email, properties: properties } } }
          },
          relationships: { list: { data: { type: 'list', id: opts.list } } }
        }
      })
    }).then(function (res) {
      if (res.status === 202) return;
      return res.text().then(function (body) {
        throw new Error('Klaviyo ' + res.status + ': ' + body);
      });
    });
  }

  function isEmail(value) {
    return EMAIL.test(value);
  }

  function bindOnce(el, fn) {
    if (el.dataset.rollyKlaviyoBound === '1') return;
    el.dataset.rollyKlaviyoBound = '1';
    fn(el);
  }

  /* ---------- 2 · Newsletter form (footer) ---------- */

  function bindNewsletter(wrap) {
    var form = wrap.querySelector('[data-klaviyo-element="form"]');
    var input = wrap.querySelector('input[type="email"]');
    var errorEl = wrap.querySelector('[data-klaviyo-element="error"]');
    var successEl = wrap.querySelector('[data-klaviyo-element="success"]');
    if (!form || !input) return;

    function showError(show) {
      if (errorEl) errorEl.hidden = !show;
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      showError(false);

      var email = (input.value || '').trim();
      if (!isEmail(email)) {
        showError(true);
        return;
      }

      subscribe({
        key: wrap.dataset.klaviyoKey,
        list: wrap.dataset.klaviyoList,
        email: email,
        source: wrap.dataset.klaviyoSource
      })
        .then(function () {
          form.hidden = true;
          showError(false);
          if (successEl) {
            successEl.hidden = false;
            successEl.focus();
          }
        })
        .catch(function (err) {
          console.error('[rolly-email-signup] newsletter subscribe failed:', err);
          showError(true);
        });
    });
  }

  /* ---------- Shopify customer forms (3 and 4) ----------
   * Both submit natively to Shopify (/contact). Shopify then redirects back to
   * the same page with ?customer_posted=true and the posting form's id as the
   * #hash. Liquid's form.posted_successfully? / form.errors are true for every
   * customer form on that page, so the hash decides which one shows the result.
   */

  var NOTIFY_FORM = '[data-pdp-notify] form';
  var NEWSLETTER_FORM = 'form[data-rolly-shopify-newsletter]';
  var EMAIL_KEY = 'rolly_notify_email';
  var posted = /[?&]customer_posted=true(&|$)/.test(window.location.search);

  function postedHere(form) {
    return form.id !== '' && window.location.hash === '#' + form.id;
  }

  function remember(email) {
    try {
      window.sessionStorage.setItem(EMAIL_KEY, email);
    } catch (err) {
      // Storage blocked: the success line falls back to "We'll email you…".
    }
  }

  function recall() {
    try {
      return window.sessionStorage.getItem(EMAIL_KEY);
    } catch (err) {
      return null;
    }
  }

  function headerOffset() {
    var header = document.querySelector('.section-header') || document.querySelector('sticky-header');
    return (header ? header.getBoundingClientRect().height : 0) + 16;
  }

  // Land `el` below the sticky header. The browser re-applies its own #hash jump
  // once the page has loaded, so the offset goes on scroll-margin-top (which
  // that jump honours) and the scroll repeats on load unless the visitor has
  // scrolled in the meantime.
  function landBelowHeader(el) {
    var userScrolled = false;
    function markScrolled() { userScrolled = true; }
    ['wheel', 'touchmove', 'keydown'].forEach(function (type) {
      window.addEventListener(type, markScrolled, { once: true, passive: true });
    });

    function land() {
      if (userScrolled) return;
      el.style.scrollMarginTop = headerOffset() + 'px';
      el.scrollIntoView({ block: 'start' });
    }

    land();
    if (document.readyState === 'complete') {
      window.requestAnimationFrame(land);
    } else {
      window.addEventListener('load', function () {
        window.requestAnimationFrame(land);
      }, { once: true });
    }
  }

  /* ---------- 3 · Notify form (PDP: sold out / worldwide) ---------- */

  // States are classes on the wrapper: is-sending, is-error, is-success.
  function notifyParts(box) {
    var form = box.querySelector('form');
    return {
      form: form,
      input: form && form.querySelector('input[type="email"]'),
      btn: form && form.querySelector('button[type="submit"]'),
      label: box.querySelector('[data-pdp-notify-label]'),
      msg: box.querySelector('.pdp-notify__msg'),
      done: box.querySelector('.pdp-notify__done')
    };
  }

  function setNotifyState(box, state, text) {
    var p = notifyParts(box);
    var sending = state === 'sending';
    box.classList.remove('is-error', 'is-sending', 'is-success');
    if (state) box.classList.add('is-' + state);
    if (p.msg) p.msg.textContent = text || '';
    if (p.label) p.label.textContent = sending ? box.dataset.labelSending : box.dataset.labelDefault;
    if (p.btn) p.btn.setAttribute('aria-busy', sending ? 'true' : 'false');
    if (p.input) {
      if (state === 'error') {
        p.input.setAttribute('aria-invalid', 'true');
      } else {
        p.input.removeAttribute('aria-invalid');
      }
    }
  }

  function bindNotify(box) {
    var p = notifyParts(box);
    if (!p.form || !p.input || !p.msg) return;

    p.input.addEventListener('input', function () {
      if (box.classList.contains('is-error')) setNotifyState(box, null);
    });

    var here = postedHere(p.form);
    var serverError = p.msg.hasAttribute('data-server-error');
    var serverSuccess = p.done && p.done.hasAttribute('data-server-success');

    if (posted && here && serverSuccess) {
      var email = recall();
      var target = box.querySelector('[data-pdp-notify-email]');
      if (email && target) target.textContent = email;
      setNotifyState(box, 'success');
      landBelowHeader(box);
      p.done.focus({ preventScroll: true });
    } else if (serverError && here) {
      box.classList.add('is-error');
      landBelowHeader(box);
    } else if (serverError) {
      // Another customer form on the page failed, not this one.
      p.msg.textContent = '';
    } else if (here) {
      // Arriving from a card (#notify): land on the form and focus the field.
      landBelowHeader(box);
      p.input.focus({ preventScroll: true });
    }
  }

  /* ---------- 4 · "Join the list" (Shopify newsletter section) ---------- */

  function setNewsletterError(form, on) {
    var input = form.querySelector('input[type="email"]');
    var msg = form.querySelector('[data-newsletter-error]');
    form.classList.toggle('is-error', on);
    if (msg) msg.hidden = !on;
    if (!input) return;
    if (on) {
      input.setAttribute('aria-invalid', 'true');
    } else {
      input.removeAttribute('aria-invalid');
    }
  }

  function bindShopifyNewsletter(form) {
    var input = form.querySelector('input[type="email"]');
    if (input) {
      input.addEventListener('input', function () {
        if (form.classList.contains('is-error')) setNewsletterError(form, false);
      });
    }

    // Another customer form posted: drop the result Liquid rendered here too.
    if (posted && !postedHere(form)) {
      form.querySelectorAll('.newsletter-form__message--success, [id^="Newsletter-error--"]').forEach(function (el) {
        el.remove();
      });
      if (input) {
        input.removeAttribute('aria-invalid');
        input.removeAttribute('autofocus');
      }
    }
  }

  /* ---------- Submit guard (forms 3 and 4) ----------
   * Capture phase on window, so it runs before Shopify's bot-protection script,
   * which can take over the submit and send the form itself. An invalid email
   * is stopped here; a valid one continues to Shopify untouched.
   */
  window.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.matches) return;
    var isNotify = form.matches(NOTIFY_FORM);
    if (!isNotify && !form.matches(NEWSLETTER_FORM)) return;

    var input = form.querySelector('input[type="email"]');
    var email = input ? (input.value || '').trim() : '';

    if (!isEmail(email)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      if (isNotify) {
        var box = form.closest('[data-pdp-notify]');
        setNotifyState(box, 'error', box.dataset.msgInvalid);
      } else {
        setNewsletterError(form, true);
      }
      if (input) input.focus();
      return;
    }

    if (input) input.value = email;
    if (isNotify) {
      remember(email);
      setNotifyState(form.closest('[data-pdp-notify]'), 'sending');
    }
  }, true);

  /* ---------- Init ---------- */

  function init() {
    document.querySelectorAll('[data-rolly-klaviyo-newsletter]').forEach(function (el) {
      bindOnce(el, bindNewsletter);
    });
    document.querySelectorAll('[data-pdp-notify]').forEach(function (el) {
      bindOnce(el, bindNotify);
    });
    document.querySelectorAll(NEWSLETTER_FORM).forEach(function (el) {
      bindOnce(el, bindShopifyNewsletter);
    });
  }

  window.rolly_klaviyo = { subscribe: subscribe, init: init };

  // Theme editor re-renders sections without a page load; bind the new markup.
  document.addEventListener('shopify:section:load', init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
